import {
    AvailablePlanType,
    ResetBenefitStatus,
} from "@common-shared/config/coverage.config"
import {
    AgenticCheckStatus,
    OneVerifyFlagType,
} from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import {
    COMMON_ONE_VERIFY_POLICIES_CONFIG,
    OneVerifyEhrWriteStatus,
    PoliciesListType,
    PolicyAssignmentPriority,
    PolicyDenialRisk,
    PolicyRangeDateFilterType,
    PolicyReassignmentReason,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import { IPoliciesListFiltersData } from "@common-shared/types/policies.types"
import { IOneVerifyAssignmentsProductivityExportRow } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types"

import type {
    IMedicaidFlagsAggregationsConfig,
    IPolicyListFilterConfig,
    IReportsFlagsAggregationsConfig,
    IReportsStatusCodesWidgetConfig,
} from "../types/objects.types"
import { CHECKER_CONFIG } from "./checker.config"

import flagHighIcon from "@front-shared/assets/images/icons/flag-high.svg"
import flagLowIcon from "@front-shared/assets/images/icons/flag-low.svg"
import flagMediumIcon from "@front-shared/assets/images/icons/flag-medium.svg"
import flagNoneIcon from "@front-shared/assets/images/icons/flag-ok.svg"
import statusFailedIcon from "@front-shared/assets/images/icons/status-failed.svg"
import statusFinishedIcon from "@front-shared/assets/images/icons/status-finished.svg"
import statusRunningIcon from "@front-shared/assets/images/icons/status-running.svg"

export enum PolicyHeaderDragDirection {
    BEFORE = "BEFORE",
    AFTER = "AFTER",
}

export enum PolicySidebarViewMode {
    CLOSED = "CLOSED",
    DEFAULT = "DEFAULT",
    EXPANDED = "EXPANDED",
}

export enum PolicySidebarTab {
    HISTORY = "HISTORY",
    COMMENTS = "COMMENTS",
}

export const POLICIES_CONFIG = {
    ...COMMON_ONE_VERIFY_POLICIES_CONFIG,

    singlePolicyHistoryAllowedRunCheckStatusCodes: [
        200, 202, 206, 429, 500, 502,
    ],

    availablePaginationOptions: [
        {
            value: 20,
            displayValue: "20 results per page",
        },
        {
            value: 50,
            displayValue: "50 results per page",
        },
        {
            value: 100,
            displayValue: "100 results per page",
        },
    ] as ISelectRenderedOption[],

    exportPoliciesBaseName: "policies",

    minMemberDataFields: 2,
    minOverallDataFields: 2, // includes 1 range dates + some field

    defaultSearchGoBackDays: 30,
    defaultReportsGoBackDays: 7,
    defaultReportsExtendedGoBackDays: 90,

    get headerTitleMapping(): Record<PoliciesListType, string> {
        return {
            [PoliciesListType.HISTORY_SEARCH]: "Search",
            [PoliciesListType.REPORTS]: "General Reports",
            [PoliciesListType.MEDICAID]: "Medicaid Reports",
            [PoliciesListType.OVERRIDES]: "Overrides",
            [PoliciesListType.PLAN_YEAR_RESETS]: "Plan Year Resets",
            [PoliciesListType.AGENTIC]: "Agentic Reports",
            [PoliciesListType.ASSIGNED_TASKS]: "Assigned Policies",
            [PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS]:
                "Assigned Policies",
            [PoliciesListType.TASK_ASSIGNMENTS]: "Policy Assignment",
            [PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS]:
                "Policy Assignment",
            [PoliciesListType.ASSIGNED_TASKS_MEDICAID]: "Assigned Policies",
            [PoliciesListType.TASK_ASSIGNMENTS_MEDICAID]: "Policy Assignment",
        }
    },

    searchMemberDataFilters: [
        "memberId",
        "memberDob",
        "memberFirstName",
        "memberLastName",
    ] as (keyof IPoliciesListFiltersData)[],

    get filtersDisplayMapping(): Record<
        keyof IPoliciesListFiltersData,
        string
    > {
        return {
            responseType: "Check type",
            checkDate: "Check date",
            nextAppointmentDate: "Next appointment date",
            planEndDate: "Plan end date",
            overrideExpirationDate: "Override expiration date",
            memberId: "Member ID",
            memberDob: "Member DOB",
            memberFirstName: "Member first name",
            memberLastName: "Member last name",
            network: "Network",
            modality: "Modality",
            modalityCode: "Service",
            payer: "Payer (ID or name)",
            patientType: "Patient type",
            patientId: "Patient ID",
            planName: "Plan name",
            groupName: "Group name",
            groupId: "Group ID",
            flags: "Alert(s)",
            hasOverrides: "With overrides only",
            planResetSoon: "With plan year reset only",
            resetBenefitsStatus: "Reset benefits status",
            planType: "Plan type",
            insuranceType: "Insurance type",
            bluecardEligibility: "BlueCard eligibility",
            officeId: "Office ID",
            primaryCarePhysicianName: "PCP Name",
            primaryCarePhysicianNpi: "PCP NPI",
            primaryCarePhysicianOrganization: "PCP Organization",
            agenticCheckStatus: "Check status",
            providerName: "Provider name",
            checkedByUsers: "Run by",
            checkedByMyselfOnly: "Run by me",

            taskAssignmentsUnassigned: "Show unassigned policies only",
            taskAssignmentAssignability: "Assignability",
            taskAssignmentStatus: "Assignment status",
            taskAssignmentAssignee: "Assignee",
            taskAssignmentDateWorked: "Last update",
            taskAssignmentAssignedBy: "Assigned by",

            customFieldAppointmentType: "Appointment type",
            customFieldRegion: "Region",
            customFieldOfficeName: "Office name",
            customFieldCarrierName: "Carrier name",
            customFieldPolicyOrder: "Policy order",
            customFieldAppointmentId: "Appointment ID",
            customFieldWorkflowStatus: "Workflow status",
            customFieldAppointmentCreatedTimestamp: "Entry date",
            customFieldCredentialingStatus: "Credentialing status",

            // backend only
            taskAssignmentExists: "",
            taskAssignmentIgnoreDoneAfterRecheck: "",
            taskAssignmentsAssigneeWithUnassigned: "",
        }
    },

    get listTypeFilters(): Record<PoliciesListType, IPolicyListFilterConfig> {
        return {
            [PoliciesListType.HISTORY_SEARCH]: {
                main: ["checkDate", "nextAppointmentDate"],
                additionalWide: ["responseType", "planEndDate", "modalityCode"],
                additional: [
                    "patientId",
                    "patientType",
                    "groupId",
                    "groupName",
                    "planType",
                    "planName",
                    "payer",
                    "insuranceType",
                    "bluecardEligibility",
                ],
            },

            [PoliciesListType.REPORTS]: {
                main: [
                    "checkDate",
                    "nextAppointmentDate",
                    "planEndDate",
                    "flags",
                    "checkedByUsers",
                    "responseType",
                ],
                additionalWide: ["modalityCode"],
                additional: [
                    "patientId",
                    "patientType",
                    "groupId",
                    "groupName",
                    "planType",
                    "planName",
                    "payer",
                    "insuranceType",
                    "officeId",
                    "bluecardEligibility",
                    "primaryCarePhysicianName",
                    "primaryCarePhysicianNpi",
                    "primaryCarePhysicianOrganization",
                ],
            },

            [PoliciesListType.OVERRIDES]: {
                main: [
                    "checkDate",
                    "overrideExpirationDate",
                    "nextAppointmentDate",
                ],
                additionalWide: ["modalityCode", "flags", "planEndDate"],
                additional: [
                    "patientId",
                    "patientType",
                    "groupId",
                    "groupName",
                    "planType",
                    "planName",
                    "payer",
                    "insuranceType",
                    "bluecardEligibility",
                ],
            },

            [PoliciesListType.MEDICAID]: {
                main: [
                    "checkDate",
                    "nextAppointmentDate",
                    "planEndDate",
                    "flags",
                    "checkedByUsers",
                ],
                additionalWide: ["modalityCode"],
                additional: [
                    "patientId",
                    "patientType",
                    "groupId",
                    "groupName",
                    "planType",
                    "planName",
                    "payer",
                    "insuranceType",
                ],
            },

            [PoliciesListType.PLAN_YEAR_RESETS]: {
                main: [
                    "checkDate",
                    "nextAppointmentDate",
                    "planEndDate",
                    "flags",
                ],
                additionalWide: ["modalityCode"],
                additional: [
                    "patientId",
                    "patientType",
                    "groupId",
                    "groupName",
                    "planType",
                    "planName",
                    "payer",
                    "insuranceType",
                    "resetBenefitsStatus",
                    "bluecardEligibility",
                ],
            },

            [PoliciesListType.AGENTIC]: {
                main: ["checkDate", "planEndDate", "flags"],
                additionalWide: ["agenticCheckStatus"],
                additional: [
                    "patientId",
                    "patientType",
                    "groupId",
                    "groupName",
                    "planType",
                    "planName",
                    "payer",
                    "insuranceType",
                    "resetBenefitsStatus",
                    "bluecardEligibility",
                ],
            },

            [PoliciesListType.ASSIGNED_TASKS]: {
                main: [
                    "checkDate",
                    {
                        key: "taskAssignmentAssignee",
                        neededPermissions: [
                            OneVerifyPermission.manageTaskAssignments,
                        ],
                    },
                    "taskAssignmentStatus",
                    "payer",
                    "flags",
                ],
                additionalWide: [
                    "planEndDate",
                    "nextAppointmentDate",
                    "overrideExpirationDate",
                    "taskAssignmentAssignedBy",
                    "taskAssignmentDateWorked",
                ],
                additional: [
                    "patientId",
                    "planType",
                    "insuranceType",
                    "officeId",
                ],
            },

            [PoliciesListType.TASK_ASSIGNMENTS]: {
                main: [
                    "checkDate",
                    "taskAssignmentAssignee",
                    "taskAssignmentStatus",
                    "payer",
                    "flags",
                ],
                additionalWide: [
                    "planEndDate",
                    "nextAppointmentDate",
                    "overrideExpirationDate",
                    "taskAssignmentAssignedBy",
                    "taskAssignmentDateWorked",
                ],
                additional: [
                    "patientId",
                    "planType",
                    "insuranceType",
                    "officeId",
                ],
            },

            [PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS]: {
                main: [
                    "checkDate",
                    "customFieldAppointmentCreatedTimestamp",
                    {
                        key: "taskAssignmentAssignee",
                        neededPermissions: [
                            OneVerifyPermission.manageTaskAssignments,
                        ],
                    },
                    "taskAssignmentStatus",
                    "payer",
                    "flags",
                ],
                additionalWide: [
                    "planEndDate",
                    "nextAppointmentDate",
                    "overrideExpirationDate",
                    "taskAssignmentAssignedBy",
                    "taskAssignmentDateWorked",
                ],
                additional: [
                    "patientId",
                    "planType",
                    "insuranceType",
                    "customFieldAppointmentType",
                    "officeId",
                    "customFieldWorkflowStatus",
                    "customFieldOfficeName",
                    "customFieldRegion",
                    "providerName",
                    "customFieldCredentialingStatus",
                    "customFieldCarrierName",
                ],
            },

            [PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS]: {
                main: [
                    "checkDate",
                    "customFieldAppointmentCreatedTimestamp",
                    "taskAssignmentAssignee",
                    "taskAssignmentStatus",
                    "payer",
                    "flags",
                ],
                additionalWide: [
                    "planEndDate",
                    "nextAppointmentDate",
                    "overrideExpirationDate",
                    "taskAssignmentAssignedBy",
                    "taskAssignmentDateWorked",
                ],
                additional: [
                    "patientId",
                    "planType",
                    "insuranceType",
                    "customFieldAppointmentType",
                    "officeId",
                    "customFieldWorkflowStatus",
                    "customFieldOfficeName",
                    "customFieldRegion",
                    "providerName",
                    "customFieldCredentialingStatus",
                    "customFieldCarrierName",
                ],
            },

            [PoliciesListType.ASSIGNED_TASKS_MEDICAID]: {
                main: [
                    "checkDate",
                    {
                        key: "taskAssignmentAssignee",
                        neededPermissions: [
                            OneVerifyPermission.manageTaskAssignments,
                        ],
                    },
                    "taskAssignmentStatus",
                    "payer",
                    "flags",
                ],
                additionalWide: [
                    "planEndDate",
                    "nextAppointmentDate",
                    "overrideExpirationDate",
                    "taskAssignmentAssignedBy",
                    "taskAssignmentDateWorked",
                ],
                additional: [
                    "patientId",
                    "planType",
                    "insuranceType",
                    "officeId",
                ],
            },

            [PoliciesListType.TASK_ASSIGNMENTS_MEDICAID]: {
                main: [
                    "checkDate",
                    "taskAssignmentAssignee",
                    "taskAssignmentAssignability",
                    "taskAssignmentStatus",
                    "payer",
                    "flags",
                ],
                additionalWide: [
                    "planEndDate",
                    "nextAppointmentDate",
                    "overrideExpirationDate",
                    "taskAssignmentAssignedBy",
                    "taskAssignmentDateWorked",
                ],
                additional: [
                    "patientId",
                    "planType",
                    "insuranceType",
                    "officeId",
                ],
            },
        }
    },

    get filtersHiddenDisplayKeys(): (keyof IPoliciesListFiltersData)[] {
        return ["hasOverrides", "planResetSoon"]
    },

    get filtersMouseFlowIgnoreFields(): (keyof IPoliciesListFiltersData)[] {
        return [
            "memberId",
            "memberDob",
            "memberFirstName",
            "memberLastName",
            "patientId",
            "planName",
            "groupName",
            "groupId",
            "primaryCarePhysicianName",
            "primaryCarePhysicianNpi",
            "primaryCarePhysicianOrganization",
            "taskAssignmentAssignee",
            "taskAssignmentAssignedBy",
        ]
    },

    get rangeDateFilterTypeMapping(): Record<
        PolicyRangeDateFilterType,
        string
    > {
        return {
            [PolicyRangeDateFilterType.TODAY]: "Today",
            [PolicyRangeDateFilterType.DAY_BEFORE]: "Since yesterday",
            [PolicyRangeDateFilterType.DAY_AFTER]: "Until tomorrow",
            [PolicyRangeDateFilterType.LAST_X_DAYS]: "Last X days",
            [PolicyRangeDateFilterType.NEXT_X_DAYS]: "Within next X days",
            [PolicyRangeDateFilterType.CUSTOM_RANGE]: "Custom date range",
        }
    },

    get pastRangeDateFilterTypes(): PolicyRangeDateFilterType[] {
        return [
            PolicyRangeDateFilterType.DAY_BEFORE,
            PolicyRangeDateFilterType.LAST_X_DAYS,
        ]
    },

    get futureRangeDateFilterTypes(): PolicyRangeDateFilterType[] {
        return [
            PolicyRangeDateFilterType.DAY_AFTER,
            PolicyRangeDateFilterType.NEXT_X_DAYS,
        ]
    },

    get policyDenialRiskIconsMapping(): Record<PolicyDenialRisk, string> {
        return {
            [PolicyDenialRisk.NONE]: flagNoneIcon,
            [PolicyDenialRisk.HIGH]: flagHighIcon,
            [PolicyDenialRisk.MEDIUM]: flagMediumIcon,
            [PolicyDenialRisk.LOW]: flagLowIcon,
        }
    },

    get medicaidAggregationsConfigs(): IMedicaidFlagsAggregationsConfig[] {
        return [
            {
                label: CHECKER_CONFIG.planTypeMapping[
                    AvailablePlanType.MANAGED_MEDICAID
                ],
                color: "#456041",
                planType: AvailablePlanType.MANAGED_MEDICAID,
            },
            {
                label: CHECKER_CONFIG.planTypeMapping[
                    AvailablePlanType.ORIGINAL_MEDICAID
                ],
                color: "#7E8AE5",
                planType: AvailablePlanType.ORIGINAL_MEDICAID,
            },
        ]
    },

    get reportsStatusCodesAggregations(): IReportsStatusCodesWidgetConfig[] {
        return [
            {
                label: "Detected Policy",
                color: NIRVANA_COLORS.brand.deepPurple,
                aggregationRules: [
                    {
                        statusCode: "200",
                    },
                    {
                        statusCode: "206",
                    },
                ],
            },
            {
                label: "Policy Not Found",
                color: NIRVANA_COLORS.brand.vibrantPurple,
                aggregationRules: [
                    {
                        statusCode: "404",
                    },
                ],
            },
            // NEEDS TO BE LAST
            {
                label: "Other Error",
                color: NIRVANA_COLORS.brand.lilacLight,
                aggregationRules: null,
            },
        ]
    },

    get reportsFlagsAggregationsConfigs(): IReportsFlagsAggregationsConfig[] {
        return [
            {
                key: "checks_with_high_risk",
                label: "High Denial Risks",
                color: NIRVANA_COLORS.brand.redDark,
                flagSeverityLower:
                    POLICIES_CONFIG.highAttentionFlagSeverityCheck,
                flagSeverityHigher: 101,
            },
            {
                key: "checks_with_medium_risk",
                label: "Potential Risks",
                color: NIRVANA_COLORS.brand.purpleMedium,
                flagSeverityLower:
                    POLICIES_CONFIG.mediumAttentionFlagSeverityCheck,
                flagSeverityHigher:
                    POLICIES_CONFIG.highAttentionFlagSeverityCheck,
            },
            {
                key: "checks_with_low_risk",
                label: "Financial Alerts",
                color: NIRVANA_COLORS.brand.blueDark,
                flagSeverityLower: 0,
                flagSeverityHigher:
                    POLICIES_CONFIG.mediumAttentionFlagSeverityCheck,
            },
        ]
    },

    get flagTypesByViewType(): Record<PoliciesListType, OneVerifyFlagType[]> {
        return {
            [PoliciesListType.MEDICAID]: [
                OneVerifyFlagType.POLICY_NOT_FOUND,
                OneVerifyFlagType.INACTIVE_POLICY,
                OneVerifyFlagType.ADDITIONAL_POLICY_DETECTED,
                OneVerifyFlagType.REVIEW_REQUIRED,
                OneVerifyFlagType.DEMOGRAPHICS_MISMATCH,
                OneVerifyFlagType.QUALIFIED_MEDICARE_BENEFICIARY,
            ],
            [PoliciesListType.HISTORY_SEARCH]: Object.values(OneVerifyFlagType),
            [PoliciesListType.REPORTS]: Object.values(OneVerifyFlagType),
            [PoliciesListType.OVERRIDES]: Object.values(OneVerifyFlagType),
            [PoliciesListType.PLAN_YEAR_RESETS]:
                Object.values(OneVerifyFlagType),
            [PoliciesListType.AGENTIC]: Object.values(OneVerifyFlagType),
            [PoliciesListType.ASSIGNED_TASKS]: Object.values(OneVerifyFlagType),
            [PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS]:
                Object.values(OneVerifyFlagType),
            [PoliciesListType.TASK_ASSIGNMENTS]:
                Object.values(OneVerifyFlagType),
            [PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS]:
                Object.values(OneVerifyFlagType),
            [PoliciesListType.ASSIGNED_TASKS_MEDICAID]:
                Object.values(OneVerifyFlagType),
            [PoliciesListType.TASK_ASSIGNMENTS_MEDICAID]:
                Object.values(OneVerifyFlagType),
        }
    },

    get planYearResetsStatusColors(): Record<ResetBenefitStatus, string> {
        return {
            [ResetBenefitStatus.DETECTED_ACTION_REQUIRED]:
                NIRVANA_COLORS.brand.primary,
            [ResetBenefitStatus.DETECTED_NO_ACTION_REQUIRED]:
                NIRVANA_COLORS.brand.green,
            [ResetBenefitStatus.NOT_DETECTED]: NIRVANA_COLORS.brand.warmShadow,
        }
    },

    get agenticCheckStatusIconsMapping(): Record<AgenticCheckStatus, string> {
        return {
            [AgenticCheckStatus.NEW]: statusRunningIcon,
            [AgenticCheckStatus.PENDING]: statusRunningIcon,
            [AgenticCheckStatus.FINISHED]: statusFinishedIcon,
            [AgenticCheckStatus.FAILED]: statusFailedIcon,
        }
    },

    get pendingAgenticStatuses(): AgenticCheckStatus[] {
        return [AgenticCheckStatus.NEW, AgenticCheckStatus.PENDING]
    },

    get policyReassignmentReasonsRendered(): ISelectRenderedOption[] {
        return Object.values(PolicyReassignmentReason).map(reason => ({
            displayValue:
                POLICIES_CONFIG.policyReassignmentReasonMapping[reason],
            value: reason,
        }))
    },

    policyAssignmentPriorityMapping(
        listType: PoliciesListType,
    ): Partial<Record<PolicyAssignmentPriority, string>> {
        switch (listType) {
            default:
                return {
                    [PolicyAssignmentPriority.LOW]: "Priority: Low",
                    [PolicyAssignmentPriority.MEDIUM]: "Priority: Medium",
                    [PolicyAssignmentPriority.HIGH]: "Priority: High",
                }
        }
    },

    getRenderedPriorityOptions(
        listType: PoliciesListType,
    ): ISelectRenderedOption[] {
        const mapping =
            POLICIES_CONFIG.policyAssignmentPriorityMapping(listType)

        return Object.values(PolicyAssignmentPriority)
            .map(priority => {
                const label = mapping[priority]

                return label
                    ? { label, value: priority, displayValue: label }
                    : null
            })
            .filter(
                (option): option is NonNullable<typeof option> =>
                    option !== null,
            )
    },

    get taskAssignmentsProductivityExportColumns(): {
        header: string
        valuePath: keyof IOneVerifyAssignmentsProductivityExportRow
        isDateTime?: boolean
    }[] {
        return [
            {
                header: "Biller Group(s)",
                valuePath: "group",
            },
            {
                header: "Assignee",
                valuePath: "assignee",
            },
            {
                header: "Last Status",
                valuePath: "lastStatus",
            },
            {
                header: "Last Update",
                valuePath: "lastUpdate",
                isDateTime: true,
            },
            {
                header: "Date Assigned",
                valuePath: "dateAssigned",
                isDateTime: true,
            },
            {
                header: "Date Started",
                valuePath: "dateStarted",
                isDateTime: true,
            },
            {
                header: "Reassign or Canceled Date",
                valuePath: "reassignOrCanceledDate",
                isDateTime: true,
            },
            {
                header: "Reassign Reason",
                valuePath: "reassignReason",
            },
            {
                header: "Total time from assignment (seconds)",
                valuePath: "totalTimeFromAssignmentSeconds",
            },
            {
                header: "Total time from start (seconds)",
                valuePath: "totalTimeFromStartSeconds",
            },
            {
                header: "Patient Name",
                valuePath: "patientName",
            },
            {
                header: "Patient DOB",
                valuePath: "patientDob",
            },
            {
                header: "Member ID",
                valuePath: "memberId",
            },
            {
                header: "Patient ID",
                valuePath: "patientId",
            },
            {
                header: "Appointment Date",
                valuePath: "appointmentDate",
            },
            {
                header: "Appointment Type",
                valuePath: "appointmentType",
            },
            {
                header: "Entry Date",
                valuePath: "entryDate",
            },
            {
                header: "Primary Carrier Name",
                valuePath: "primaryCarrierName",
            },
            {
                header: "Office Name",
                valuePath: "officeName",
            },
            {
                header: "Office ID",
                valuePath: "officeId",
            },
        ]
    },

    get oneVerifyEhrWriteStatusColorMapping(): Record<
        OneVerifyEhrWriteStatus,
        string
    > {
        return {
            [OneVerifyEhrWriteStatus.COMPLETED]: NIRVANA_COLORS.brand.green,
            [OneVerifyEhrWriteStatus.FAILED]: NIRVANA_COLORS.brand.error,
            [OneVerifyEhrWriteStatus.PENDING]: NIRVANA_COLORS.brand.orange,
            [OneVerifyEhrWriteStatus.CREATED]: NIRVANA_COLORS.brand.orange,
            [OneVerifyEhrWriteStatus.ELIGIBILITY_STARTED]:
                NIRVANA_COLORS.brand.orange,
            [OneVerifyEhrWriteStatus.ELIGIBILITY_COMPLETED]:
                NIRVANA_COLORS.brand.orange,
        }
    },
}
